import { useEffect, useState, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import "@/i18n";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import ProfileCompletionModal, { needsProfileCompletion } from "@/components/ProfileCompletionModal";
import { requestNotificationPermission, onForegroundMessage } from "@/lib/firebase";

// Pages
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import MemberDashboardPage from "@/pages/MemberDashboardPage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import RegisterBusinessPage from "@/pages/RegisterBusinessPage";
import TrainersListPage from "@/pages/TrainersListPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import PresentationPage from "@/pages/PresentationPage";
import PublicBookingPage from "@/pages/PublicBookingPage";
import { LocalizedDataProvider } from "@/contexts/LocalizedDataContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
  console.error("REACT_APP_BACKEND_URL is not set in environment variables!");
}
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fcmToken, setFcmToken] = useState(null);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers,
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Request push notification permission and register FCM token
        if ('Notification' in window && Notification.permission !== 'denied') {
          setTimeout(() => {
            registerPushNotifications(token);
          }, 3000); // Delay to not overwhelm user on login
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Register push notifications
  const registerPushNotifications = async (authToken) => {
    try {
      const fcmTokenValue = await requestNotificationPermission();
      if (fcmTokenValue) {
        setFcmToken(fcmTokenValue);
        // Send FCM token to backend
        const token = authToken || localStorage.getItem('auth_token');
        if (token) {
          await fetch(`${API}/auth/fcm-token`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({ fcm_token: fcmTokenValue })
          });
          console.log('FCM token registered successfully');
        }
      }
    } catch (error) {
      console.error('Error registering push notifications:', error);
    }
  };

  const logout = async () => {
    try {
      // Unregister FCM token on logout
      if (fcmToken) {
        const token = localStorage.getItem('auth_token');
        if (token) {
          await fetch(`${API}/auth/fcm-token`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            credentials: 'include',
            body: JSON.stringify({ fcm_token: fcmToken })
          });
        }
      }
      
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.error(e);
    }
    // Clear localStorage token
    localStorage.removeItem('auth_token');
    setUser(null);
    setFcmToken(null);
  };

  useEffect(() => {
    checkAuth();
    
    // Listen for foreground push notifications
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('Foreground notification received:', payload);
      const title = payload.notification?.title || payload.data?.title || 'Yeni Bildirim';
      const body = payload.notification?.body || payload.data?.body || '';
      
      // Show toast notification
      toast.info(title, {
        description: body,
        duration: 6000,
        action: {
          label: 'Görüntüle',
          onClick: () => {
            // Navigate to reservations tab
            window.location.href = '/member-dashboard?tab=reservations';
          }
        }
      });
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  return { user, setUser, loading, logout, checkAuth, fcmToken };
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user data was passed from AuthCallback (social login), use it
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      // Only show profile modal for NEW social login users
      const isNewUser = location.state?.isNewUser || location.state?.showProfileComplete;
      if (isNewUser && needsProfileCompletion(location.state.user, true)) {
        setShowProfileModal(true);
      }
      return;
    }

    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include",
          headers,
        });
        if (!response.ok) throw new Error("Not authenticated");
        const userData = await response.json();
        setIsAuthenticated(true);
        setUser(userData);
        // Don't show profile modal for existing users - only for new social logins
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/auth");
      }
    };
    checkAuth();
  }, [navigate, location.state]);

  const handleProfileComplete = (updatedUser) => {
    setUser(updatedUser);
    setShowProfileModal(false);
  };

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <>
      {showProfileModal && (
        <ProfileCompletionModal
          isOpen={showProfileModal}
          user={user}
          onComplete={handleProfileComplete}
        />
      )}
      {children({ user })}
    </>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - SYNCHRONOUS check during render
  // This prevents race conditions by processing session_id FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/explore" element={<LandingPage guestMode={true} />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/business/:id" element={<BusinessProfilePage />} />
      <Route path="/trainers" element={<TrainersListPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/sunum" element={<PresentationPage />} />
      <Route path="/tanitim" element={<PresentationPage />} />
      <Route path="/rezervasyon/:token" element={<PublicBookingPage />} />
      <Route
        path="/dashboard/*"
        element={
          <ProtectedRoute>
            {({ user }) => <DashboardPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/register-business"
        element={
          <ProtectedRoute>
            {({ user }) => <RegisterBusinessPage user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/member/*"
        element={
          <ProtectedRoute>
            {({ user }) => <MemberDashboardPage user={user} />}
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LocalizedDataProvider>
        <CurrencyProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </CurrencyProvider>
        </LocalizedDataProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
